import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../../../Header';
import Footer from '../../../Footer';
import { incomingShipmentApi } from '../../../../api/incomingShipmentApi';
import { adminProductApi } from '../../../../api/adminProductApi';
import { maybeResizeImageFile } from '../../../../utils/imageUploadUtils';
import { checkIsAdminLoggined } from '../../../../utils/utils';
import { getApiErrorText } from '../../../../utils/apiResponse';
import { parseInventoryMoney } from '../../../../utils/inventoryInputs';
import { queuedIncomingApi } from '../../../../utils/incomingRequestQueue';
import IncomingShipmentDetailDesktopView from './IncomingShipmentDetailDesktopView';
import IncomingShipmentDetailMobileView from './IncomingShipmentDetailMobileView';
import IncomingDeleteItemConfirmModal from './IncomingDeleteItemConfirmModal';
import IncomingShipmentExcelImportModal from './IncomingShipmentExcelImportModal';

const pcm16ToWavDataUrl = (pcm16, sampleRate) => {
  const n = pcm16.length;
  const buf = new ArrayBuffer(44 + n * 2);
  const v = new DataView(buf);
  const wStr = (o, s) => {
    for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i));
  };
  wStr(0, 'RIFF');
  v.setUint32(4, 36 + n * 2, true);
  wStr(8, 'WAVE');
  wStr(12, 'fmt ');
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);
  v.setUint16(22, 1, true);
  v.setUint32(24, sampleRate, true);
  v.setUint32(28, sampleRate * 2, true);
  v.setUint16(32, 2, true);
  v.setUint16(34, 16, true);
  wStr(36, 'data');
  v.setUint32(40, n * 2, true);
  let o = 44;
  for (let i = 0; i < n; i++) {
    v.setInt16(o, pcm16[i], true);
    o += 2;
  }
  const u8 = new Uint8Array(buf);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < u8.length; i += chunk) {
    binary += String.fromCharCode.apply(null, u8.subarray(i, i + chunk));
  }
  return `data:audio/wav;base64,${btoa(binary)}`;
};

const genTonePcm16 = (freq, durationSec, sampleRate, amplitude = 0.22) => {
  const n = Math.floor(sampleRate * durationSec);
  const pcm = new Int16Array(n);
  for (let i = 0; i < n; i++) {
    const t = i / sampleRate;
    const attack = Math.min(1, i / 8);
    const rel = i > n - 24 ? (n - i) / 24 : 1;
    const env = attack * rel;
    pcm[i] = Math.round(Math.min(1, amplitude * env) * 32767 * Math.sin(2 * Math.PI * freq * t));
  }
  return pcm;
};

const playIncomingScanFeedbackHtml5 = (foundInCatalog) => {
  const sr = 8000;
  const play = (freq, dur, amplitude = 0.22) => {
    const url = pcm16ToWavDataUrl(genTonePcm16(freq, dur, sr, amplitude), sr);
    const a = new Audio(url);
    a.volume = 1;
    a.play().catch(() => {});
  };
  if (foundInCatalog) {
    play(523.25, 0.1);
    window.setTimeout(() => play(783.99, 0.14), 110);
  } else {
    const errAmp = 1;
    play(520, 0.28, errAmp);
    window.setTimeout(() => play(390, 0.32, errAmp), 220);
    window.setTimeout(() => play(260, 0.42, errAmp), 500);
  }
};

// Each visit owns its state and queue. Ignore completions after that visit unmounts.
const useShipmentState = (initialValue, mounted) => {
  const [value, setValue] = useState(initialValue);
  const setCurrentValue = useCallback(next => {
    if (!mounted.current) return;
    setValue(previous => {
      if (!mounted.current) return previous;
      return typeof next === 'function' ? next(previous) : next;
    });
  }, [mounted]);
  return [value, setCurrentValue];
};

const IncomingShipmentDetail = props => {
  const { shipmentId } = useParams();
  const shipmentApi = useMemo(() => queuedIncomingApi(incomingShipmentApi), []);
  const pendingScans = useRef(0);
  const postingLock = useRef(false);
  const loadSequence = useRef(0);
  const audioCtxRef = useRef(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (audioCtxRef.current) audioCtxRef.current.close().catch(() => {});
    };
  }, []);
  const inputRef = useRef(null);
  const audioPrimedRef = useRef(false);

  const [shipment, setShipment] = useShipmentState(null, mounted);
  const [barcode, setBarcode] = useShipmentState('', mounted);
  const [submitting, setSubmitting] = useShipmentState(false, mounted);
  const [loading, setLoading] = useShipmentState(false, mounted);
  const [error, setError] = useShipmentState('', mounted);
  const [bindItemId, setBindItemId] = useShipmentState(null, mounted);
  const [bindSearch, setBindSearch] = useShipmentState('', mounted);
  const [bindSearchLoading, setBindSearchLoading] = useShipmentState(false, mounted);
  const [bindSearchResults, setBindSearchResults] = useShipmentState([], mounted);
  const [bindSubmitting, setBindSubmitting] = useShipmentState(false, mounted);
  const [itemPhotoUploadingId, setItemPhotoUploadingId] = useShipmentState(null, mounted);
  const [itemDeletingId, setItemDeletingId] = useShipmentState(null, mounted);
  const [deleteConfirmExpectedId, setDeleteConfirmExpectedId] = useShipmentState(null, mounted);
  const [itemQuantitySavingId, setItemQuantitySavingId] = useShipmentState(null, mounted);
  const [addingExpected, setAddingExpected] = useShipmentState(false, mounted);
  const [deletingExpectedId, setDeletingExpectedId] = useShipmentState(null, mounted);
  const [savingExpectedBarcodeId, setSavingExpectedBarcodeId] = useShipmentState(null, mounted);
  const [bindingExpectedId, setBindingExpectedId] = useShipmentState(null, mounted);
  const [excelImportOpen, setExcelImportOpen] = useShipmentState(false, mounted);
  const [savingShipmentStatus, setSavingShipmentStatus] = useShipmentState(false, mounted);
  const [savingExpectedStatusId, setSavingExpectedStatusId] = useShipmentState(null, mounted);
  const [postingExpectedToSiteId, setPostingExpectedToSiteId] = useShipmentState(null, mounted);
  const [postingAllReady, setPostingAllReady] = useShipmentState(false, mounted);
  const [generatingExpectedFromScanned, setGeneratingExpectedFromScanned] = useShipmentState(false, mounted);
  const [savingExpectedPurchasePriceId, setSavingExpectedPurchasePriceId] = useShipmentState(null, mounted);

  const parseItemTime = (item) => {
    if (!item) return 0;
    if (item.scannedAtUtc) {
      const ts = Date.parse(item.scannedAtUtc);
      if (!Number.isNaN(ts)) return ts;
    }
    const raw = String(item.scannedAt || '').trim();
    const m = raw.match(/^(\d{1,2})\.(\d{1,2}),\s*(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/);
    if (!m) return 0;
    const now = new Date();
    const dd = Number(m[1]);
    const mm = Number(m[2]) - 1;
    const hh = Number(m[3]);
    const mi = Number(m[4]);
    const ss = Number(m[5] || 0);
    return new Date(now.getFullYear(), mm, dd, hh, mi, ss).getTime();
  };

  const sortItemsNewestFirst = (items) => {
    return [...(items || [])].sort((a, b) => {
      const t = parseItemTime(b) - parseItemTime(a);
      if (t !== 0) return t;
      return (b.id || 0) - (a.id || 0);
    });
  };

  const mergeDetailApiPayload = (prev, raw) => {
    if (!prev) return prev;
    const p = raw == null ? {} : (Array.isArray(raw) ? { expectedItems: raw } : raw);
    return {
      ...prev,
      ...(p.expectedItems != null ? { expectedItems: p.expectedItems } : {}),
      ...(p.scannedItems != null ? { scannedItems: sortItemsNewestFirst(p.scannedItems) } : {}),
      ...(typeof p.shipmentStatus === 'string' ? { status: p.shipmentStatus } : {}),
      ...(p.files != null ? { files: p.files } : {})
    };
  };

  const focusScanInput = () => {
    const el = inputRef.current;
    if (!el) return;
    const run = () => {
      try {
        el.focus({ preventScroll: true });
      } catch (e) {
        el.focus();
      }
    };
    run();
    window.requestAnimationFrame(run);
    window.setTimeout(run, 0);
    window.setTimeout(run, 80);
  };

  const playTone = (ctx, freq, startTime, duration, type = 'sine', peakGain = 0.11) => {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, startTime);
    g.gain.setValueAtTime(0.0001, startTime);
    g.gain.exponentialRampToValueAtTime(peakGain, startTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);
    osc.connect(g);
    g.connect(ctx.destination);
    osc.start(startTime);
    osc.stop(startTime + duration + 0.03);
  };

  const playIncomingScanFeedback = (foundInCatalog) => {
    const scheduleWeb = (ctx) => {
      const t0 = ctx.currentTime;
      if (foundInCatalog) {
        playTone(ctx, 523.25, t0, 0.1, 'sine');
        playTone(ctx, 783.99, t0 + 0.11, 0.14, 'sine');
      } else {
        const errGain = 0.9;
        playTone(ctx, 520, t0, 0.28, 'sawtooth', errGain);
        playTone(ctx, 390, t0 + 0.22, 0.32, 'sawtooth', errGain);
        playTone(ctx, 260, t0 + 0.5, 0.42, 'triangle', errGain);
      }
    };
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) {
        playIncomingScanFeedbackHtml5(foundInCatalog);
        return;
      }
      if (!audioCtxRef.current) {
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      ctx.resume()
        .then(() => {
          if (!mounted.current) return;
          if (ctx.state === 'running') {
            scheduleWeb(ctx);
          } else {
            playIncomingScanFeedbackHtml5(foundInCatalog);
          }
        })
        .catch(() => {
          if (mounted.current) playIncomingScanFeedbackHtml5(foundInCatalog);
        });
    } catch (e) {
      playIncomingScanFeedbackHtml5(foundInCatalog);
    }
  };

  const loadShipment = () => {
    const sequence = ++loadSequence.current;
    const isLatestRequest = () => mounted.current && sequence === loadSequence.current;
    setLoading(true);
    shipmentApi.getShipmentDetail(shipmentId)
      .then((res) => {
        if (!isLatestRequest()) return;
        const payload = res.data?.data || null;
        if (!payload) {
          setShipment(null);
          return;
        }
        setShipment({
          ...payload,
          scannedItems: sortItemsNewestFirst(payload.scannedItems || []),
          expectedItems: payload.expectedItems || []
        });
      })
      .catch((e) => {
        if (isLatestRequest()) setError(getApiErrorText(e, 'Не вдалося виконати операцію з поставкою'));
      })
      .finally(() => {
        if (isLatestRequest()) setLoading(false);
      });
  };

  useEffect(() => {
    checkIsAdminLoggined(props);
    loadShipment();
  }, [shipmentId]);

  useEffect(() => {
    const unlock = () => {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        if (!audioCtxRef.current) {
          audioCtxRef.current = new Ctx();
        }
        const ctx = audioCtxRef.current;
        void ctx.resume();
        if (!audioPrimedRef.current) {
          audioPrimedRef.current = true;
          const buf = ctx.createBuffer(1, 1, 8000);
          const src = ctx.createBufferSource();
          src.buffer = buf;
          src.connect(ctx.destination);
          src.start(0);
        }
      } catch (e) {
        /* ignore */
      }
    };
    window.addEventListener('pointerdown', unlock, { passive: true, capture: true });
    window.addEventListener('keydown', unlock, { passive: true, capture: true });
    return () => {
      window.removeEventListener('pointerdown', unlock, { capture: true });
      window.removeEventListener('keydown', unlock, { capture: true });
    };
  }, []);

  useEffect(() => {
    if (!loading && shipment) {
      focusScanInput();
    }
  }, [loading, shipment?.id]);

  const submitScan = (codeOverride) => {
    const code = (codeOverride != null ? codeOverride : barcode || '').trim();
    if (!code) return;
    pendingScans.current += 1;
    setBarcode('');
    setSubmitting(true);
    setError('');
    shipmentApi.scanBarcode(shipmentId, code)
      .then((res) => {
        if (!mounted.current) return;
        const data = res.data?.data;
        const payload = Array.isArray(data) ? { scannedItems: data } : (data || {});
        const sorted = sortItemsNewestFirst(payload.scannedItems || []);
        setShipment((prev) => mergeDetailApiPayload(prev, {
          ...payload,
          scannedItems: sorted
        }));
        const newest = sorted[0];
        const isFound = Boolean(newest && newest.foundInCatalog);
        if (navigator?.vibrate) {
          navigator.vibrate(isFound ? 30 : [70, 40, 70]);
        }
        playIncomingScanFeedback(isFound);
      })
      .catch((e) => {
        if (!mounted.current) return;
        setError(getApiErrorText(e, 'Не вдалося виконати операцію з поставкою'));
        playIncomingScanFeedback(false);
        if (navigator?.vibrate) {
          navigator.vibrate([80, 50, 80]);
        }
      })
      .finally(() => {
        pendingScans.current = Math.max(0, pendingScans.current - 1);
        setSubmitting(pendingScans.current > 0);
        focusScanInput();
        window.setTimeout(focusScanInput, 0);
        window.setTimeout(focusScanInput, 120);
      });
  };

  const openBindDropdown = (itemId) => {
    setBindItemId(itemId);
    setBindSearch('');
    setBindSearchResults([]);
  };

  const closeBindDropdown = () => {
    if (!mounted.current) return;
    setBindItemId(null);
    setBindSearch('');
    setBindSearchResults([]);
    setBindSearchLoading(false);
  };

  const uploadItemPhoto = async (itemId, file) => {
    if (!file || !itemId) return;
    const processedFile = await maybeResizeImageFile(file, {
      onlyMobile: true,
      maxWidth: 1920,
      maxHeight: 1920,
      quality: 0.82,
      minBytes: 700 * 1024
    });
    const fd = new FormData();
    fd.append('file', processedFile);
    setItemPhotoUploadingId(itemId);
    setError('');
    shipmentApi.uploadScannedItemPhoto(shipmentId, itemId, fd)
      .then((res) => setShipment((prev) => ({ ...prev, scannedItems: sortItemsNewestFirst(res.data?.data || []) })))
      .catch((e) => setError(getApiErrorText(e, 'Не вдалося виконати операцію з поставкою')))
      .finally(() => setItemPhotoUploadingId(null));
  };

  const deleteItemPhoto = (itemId) => {
    if (!itemId) return;
    setItemPhotoUploadingId(itemId);
    setError('');
    shipmentApi.deleteScannedItemPhoto(shipmentId, itemId)
      .then((res) => setShipment((prev) => ({ ...prev, scannedItems: sortItemsNewestFirst(res.data?.data || []) })))
      .catch((e) => setError(getApiErrorText(e, 'Не вдалося виконати операцію з поставкою')))
      .finally(() => setItemPhotoUploadingId(null));
  };

  const patchShipmentStatusDetail = (status) => {
    if (!status || savingShipmentStatus) return;
    setSavingShipmentStatus(true);
    setError('');
    shipmentApi.patchShipment(shipmentId, status)
      .then((res) => {
        const next = res.data?.data?.status ?? status;
        setShipment((prev) => (prev ? { ...prev, status: next } : prev));
      })
      .catch((e) => setError(getApiErrorText(e, 'Не вдалося виконати операцію з поставкою')))
      .finally(() => setSavingShipmentStatus(false));
  };

  const postExpectedToSite = async (expectedItemId) => {
    if (expectedItemId == null || postingLock.current) return;
    postingLock.current = true;
    setPostingExpectedToSiteId(expectedItemId);
    setError('');
    try {
      const res = await shipmentApi.postExpectedItemToSite(shipmentId, expectedItemId);
      setShipment((prev) => mergeDetailApiPayload(prev, res.data?.data));
    } catch (e) {
      setError(getApiErrorText(e, 'Не вдалося виконати операцію з поставкою'));
    } finally {
      postingLock.current = false;
      setPostingExpectedToSiteId(null);
    }
  };

  const postAllReadyExpectedToSite = async () => {
    if (postingLock.current) return;
    postingLock.current = true;
    setPostingAllReady(true);
    setError('');
    try {
      const res = await shipmentApi.postAllReadyExpectedToSite(shipmentId);
      setShipment((prev) => mergeDetailApiPayload(prev, res.data?.data));
    } catch (e) {
      setError(getApiErrorText(e, 'Не вдалося виконати операцію з поставкою'));
    } finally {
      postingLock.current = false;
      setPostingAllReady(false);
    }
  };

  const generateExpectedFromScanned = async () => {
    if (generatingExpectedFromScanned) return;
    setGeneratingExpectedFromScanned(true);
    setError('');
    try {
      const res = await shipmentApi.generateExpectedFromScanned(shipmentId);
      setShipment((prev) => mergeDetailApiPayload(prev, res.data?.data));
    } catch (e) {
      setError(getApiErrorText(e, 'Не вдалося виконати операцію з поставкою'));
    } finally {
      setGeneratingExpectedFromScanned(false);
    }
  };

  const saveExpectedPurchasePrice = async (expectedItem, rawValue) => {
    if (!expectedItem?.id || savingExpectedPurchasePriceId != null) return;
    const trimmed = rawValue == null ? '' : String(rawValue).trim();
    let payload = null;
    if (trimmed !== '') {
      const n = parseInventoryMoney(trimmed);
      if (n == null || n <= 0) {
        setError('Вкажіть коректну ціну закупки (більше 0)');
        throw new Error('invalid price');
      }
      payload = n;
    }
    setSavingExpectedPurchasePriceId(expectedItem.id);
    setError('');
    try {
      const res = await shipmentApi.patchExpectedItemPurchasePrice(shipmentId, expectedItem.id, payload);
      setShipment((prev) => mergeDetailApiPayload(prev, res.data?.data));
    } catch (e) {
      setError(getApiErrorText(e, 'Не вдалося виконати операцію з поставкою'));
      throw e;
    } finally {
      setSavingExpectedPurchasePriceId(null);
    }
  };

  const patchExpectedItemStatus = async (expectedItemId, status) => {
    if (expectedItemId == null || !status || savingExpectedStatusId != null) return;
    setSavingExpectedStatusId(expectedItemId);
    setError('');
    try {
      const res = await shipmentApi.patchExpectedItem(shipmentId, expectedItemId, { status });
      setShipment((prev) => mergeDetailApiPayload(prev, res.data?.data));
    } catch (e) {
      setError(getApiErrorText(e, 'Не вдалося виконати операцію з поставкою'));
    } finally {
      setSavingExpectedStatusId(null);
    }
  };

  const bindProductToItem = (productId) => {
    if (!bindItemId || !productId || bindSubmitting) return;
    setBindSubmitting(true);
    setError('');
    shipmentApi.bindScannedItemProduct(shipmentId, bindItemId, productId)
      .then((res) => {
        const data = res.data?.data;
        const payload = Array.isArray(data) ? { scannedItems: data } : (data || {});
        setShipment((prev) => mergeDetailApiPayload(prev, {
          ...payload,
          scannedItems: sortItemsNewestFirst(payload.scannedItems || [])
        }));
        closeBindDropdown();
      })
      .catch((e) => setError(getApiErrorText(e, 'Не вдалося виконати операцію з поставкою')))
      .finally(() => setBindSubmitting(false));
  };

  const deleteScannedItem = (itemId) => {
    if (!itemId || itemDeletingId) return;
    setItemDeletingId(itemId);
    setError('');
    shipmentApi.deleteScannedItem(shipmentId, itemId)
      .then((res) => {
        const data = res.data?.data;
        const payload = Array.isArray(data) ? { scannedItems: data } : (data || {});
        if (bindItemId === itemId) closeBindDropdown();
        setShipment((prev) => mergeDetailApiPayload(prev, {
          ...payload,
          scannedItems: sortItemsNewestFirst(payload.scannedItems || [])
        }));
        focusScanInput();
        window.setTimeout(focusScanInput, 0);
      })
      .catch((e) => setError(getApiErrorText(e, 'Не вдалося виконати операцію з поставкою')))
      .finally(() => setItemDeletingId(null));
  };

  const deleteExpectedHighlight = useMemo(() => {
    if (!deleteConfirmExpectedId || !shipment?.expectedItems) return '';
    const it = shipment.expectedItems.find((x) => x.id === deleteConfirmExpectedId);
    if (!it) return '';
    const code = (it.productCode || '').trim();
    const name = (it.name || '').trim();
    const bc = (it.barcode || '').trim();
    if (code && name) return `${code} — ${name}`;
    if (name) return name;
    if (code) return code;
    if (bc) return bc;
    return `#${it.id}`;
  }, [deleteConfirmExpectedId, shipment]);

  const cancelDeleteExpectedItem = () => {
    if (deletingExpectedId) return;
    setDeleteConfirmExpectedId(null);
  };

  const requestDeleteExpectedItem = (expectedItemId) => {
    if (expectedItemId == null || deletingExpectedId) return;
    setDeleteConfirmExpectedId(expectedItemId);
  };

  const confirmDeleteExpectedItem = () => {
    const id = deleteConfirmExpectedId;
    if (id == null || deletingExpectedId) return;
    setDeletingExpectedId(id);
    setError('');
    shipmentApi.deleteExpectedItem(shipmentId, id)
      .then((res) => {
        setShipment((prev) => mergeDetailApiPayload(prev, res.data?.data));
        if (mounted.current) setDeleteConfirmExpectedId(null);
      })
      .catch((e) => setError(getApiErrorText(e, 'Не вдалося виконати операцію з поставкою')))
      .finally(() => setDeletingExpectedId(null));
  };

  const saveItemQuantity = (itemId, quantity) => {
    if (!itemId || itemQuantitySavingId) return;
    setItemQuantitySavingId(itemId);
    setError('');
    shipmentApi.updateScannedItemQuantity(shipmentId, itemId, quantity)
      .then((res) => {
        const data = res.data?.data;
        const payload = Array.isArray(data) ? { scannedItems: data } : (data || {});
        setShipment((prev) => mergeDetailApiPayload(prev, {
          ...payload,
          scannedItems: sortItemsNewestFirst(payload.scannedItems || [])
        }));
        focusScanInput();
        window.setTimeout(focusScanInput, 0);
      })
      .catch((e) => setError(getApiErrorText(e, 'Не вдалося виконати операцію з поставкою')))
      .finally(() => setItemQuantitySavingId(null));
  };

  const onAddExpectedItem = async (body) => {
    setError('');
    setAddingExpected(true);
    try {
      const res = await shipmentApi.addExpectedItem(shipmentId, body);
      setShipment((prev) => mergeDetailApiPayload(prev, res.data?.data));
    } catch (e) {
      setError(getApiErrorText(e, 'Не вдалося виконати операцію з поставкою'));
      throw e;
    } finally {
      setAddingExpected(false);
    }
  };

  const saveExpectedItemBarcode = async (expectedItem, rawValue) => {
    const trimmed = rawValue == null ? '' : String(rawValue).trim();
    const payloadBarcode = trimmed === '' ? null : trimmed;
    setError('');
    setSavingExpectedBarcodeId(expectedItem.id);
    try {
      const res = await shipmentApi.patchExpectedItemBarcode(shipmentId, expectedItem.id, payloadBarcode);
      setShipment((prev) => mergeDetailApiPayload(prev, res.data?.data));
    } catch (e) {
      setError(getApiErrorText(e, 'Не вдалося виконати операцію з поставкою'));
      throw e;
    } finally {
      setSavingExpectedBarcodeId(null);
    }
  };

  const bindExpectedItemToProduct = async (expectedItemId, productId) => {
    setError('');
    setBindingExpectedId(expectedItemId);
    try {
      const res = await shipmentApi.bindExpectedItemProduct(shipmentId, expectedItemId, productId);
      setShipment((prev) => mergeDetailApiPayload(prev, res.data?.data));
    } catch (e) {
      setError(getApiErrorText(e, 'Не вдалося виконати операцію з поставкою'));
      throw e;
    } finally {
      setBindingExpectedId(null);
    }
  };

  useEffect(() => {
    const code = (barcode || '').trim();
    if (!code) return;
    const timer = setTimeout(() => submitScan(code), 220);
    return () => clearTimeout(timer);
  }, [barcode, shipmentId]);

  useEffect(() => {
    const q = (bindSearch || '').trim();
    if (!bindItemId || q.length < 2) {
      setBindSearchResults([]);
      setBindSearchLoading(false);
      return;
    }
    setBindSearchLoading(true);
    let active = true;
    const timer = setTimeout(() => {
      adminProductApi.searchProductsInfo(q)
        .then((res) => { if (active) setBindSearchResults(res.data?.data || []); })
        .catch(() => { if (active) setBindSearchResults([]); })
        .finally(() => { if (active) setBindSearchLoading(false); });
    }, 220);
    return () => { active = false; clearTimeout(timer); };
  }, [bindSearch, bindItemId]);

  useEffect(() => {
    if (!bindItemId) return;
    const onDocumentClick = (event) => {
      const target = event.target;
      if (target && target.closest && target.closest('[data-bind-search-input="true"]')) {
        return;
      }
      if (target && target.closest && target.closest('[data-bind-open-btn="true"]')) {
        return;
      }
      if (target && target.closest && target.closest('[data-incoming-photo-area="true"]')) {
        return;
      }
      if (target && target.closest && target.closest('[data-incoming-item-action="true"]')) {
        return;
      }
      if (target && target.closest && target.closest('[data-incoming-expected-block="true"]')) {
        return;
      }
      closeBindDropdown();
    };
    document.addEventListener('click', onDocumentClick);
    return () => document.removeEventListener('click', onDocumentClick);
  }, [bindItemId]);

  const shipmentForViews = shipment
    ? { ...shipment, scannedItems: shipment.scannedItems }
    : null;

  return (
    <>
      <div className='container'>
        <div className='row'>
          <div className='col p-0'>
            <Header />
          </div>
        </div>

        <div className='row mt-3'>
          <div className='col-12 d-none d-lg-block p-0'>
            <IncomingShipmentDetailDesktopView
              shipmentId={shipmentId}
              shipment={shipmentForViews}
              barcode={barcode}
              setBarcode={setBarcode}
              submitScan={submitScan}
              inputRef={inputRef}
              submitting={submitting}
              loading={loading}
              error={error}
              bindItemId={bindItemId}
              bindSearch={bindSearch}
              setBindSearch={setBindSearch}
              bindSearchResults={bindSearchResults}
              bindSearchLoading={bindSearchLoading}
              bindSubmitting={bindSubmitting}
              openBindDropdown={openBindDropdown}
              bindProductToItem={bindProductToItem}
              itemPhotoUploadingId={itemPhotoUploadingId}
              uploadItemPhoto={uploadItemPhoto}
              deleteItemPhoto={deleteItemPhoto}
              deleteScannedItem={deleteScannedItem}
              saveItemQuantity={saveItemQuantity}
              itemDeletingId={itemDeletingId}
              itemQuantitySavingId={itemQuantitySavingId}
              onAddExpectedItem={onAddExpectedItem}
              onDeleteExpectedItem={requestDeleteExpectedItem}
              addingExpected={addingExpected}
              deletingExpectedId={deletingExpectedId}
              onOpenExcelImport={() => setExcelImportOpen(true)}
              onSaveExpectedBarcode={saveExpectedItemBarcode}
              savingExpectedBarcodeId={savingExpectedBarcodeId}
              onBindExpectedItem={bindExpectedItemToProduct}
              bindingExpectedId={bindingExpectedId}
              onPatchShipmentStatus={patchShipmentStatusDetail}
              savingShipmentStatus={savingShipmentStatus}
              onChangeExpectedItemStatus={patchExpectedItemStatus}
              savingExpectedStatusId={savingExpectedStatusId}
              onPostExpectedToSite={postExpectedToSite}
              postingExpectedToSiteId={postingExpectedToSiteId}
              onPostAllReadyToSite={postAllReadyExpectedToSite}
              postingAllReady={postingAllReady}
              expectedDeleteConfirmOpen={deleteConfirmExpectedId != null}
              onGenerateExpectedFromScanned={generateExpectedFromScanned}
              generatingExpectedFromScanned={generatingExpectedFromScanned}
              onSaveExpectedPurchasePrice={saveExpectedPurchasePrice}
              savingExpectedPurchasePriceId={savingExpectedPurchasePriceId}
            />
          </div>
          <div className='col-12 d-lg-none'>
            <IncomingShipmentDetailMobileView
              shipmentId={shipmentId}
              shipment={shipmentForViews}
              barcode={barcode}
              setBarcode={setBarcode}
              submitScan={submitScan}
              inputRef={inputRef}
              submitting={submitting}
              loading={loading}
              error={error}
              bindItemId={bindItemId}
              bindSearch={bindSearch}
              setBindSearch={setBindSearch}
              bindSearchResults={bindSearchResults}
              bindSearchLoading={bindSearchLoading}
              bindSubmitting={bindSubmitting}
              openBindDropdown={openBindDropdown}
              bindProductToItem={bindProductToItem}
              itemPhotoUploadingId={itemPhotoUploadingId}
              uploadItemPhoto={uploadItemPhoto}
              deleteItemPhoto={deleteItemPhoto}
              deleteScannedItem={deleteScannedItem}
              saveItemQuantity={saveItemQuantity}
              itemDeletingId={itemDeletingId}
              itemQuantitySavingId={itemQuantitySavingId}
              onAddExpectedItem={onAddExpectedItem}
              onDeleteExpectedItem={requestDeleteExpectedItem}
              addingExpected={addingExpected}
              deletingExpectedId={deletingExpectedId}
              onOpenExcelImport={() => setExcelImportOpen(true)}
              onSaveExpectedBarcode={saveExpectedItemBarcode}
              savingExpectedBarcodeId={savingExpectedBarcodeId}
              onBindExpectedItem={bindExpectedItemToProduct}
              bindingExpectedId={bindingExpectedId}
              onPatchShipmentStatus={patchShipmentStatusDetail}
              savingShipmentStatus={savingShipmentStatus}
              onChangeExpectedItemStatus={patchExpectedItemStatus}
              savingExpectedStatusId={savingExpectedStatusId}
              onPostExpectedToSite={postExpectedToSite}
              postingExpectedToSiteId={postingExpectedToSiteId}
              onPostAllReadyToSite={postAllReadyExpectedToSite}
              postingAllReady={postingAllReady}
              expectedDeleteConfirmOpen={deleteConfirmExpectedId != null}
              onGenerateExpectedFromScanned={generateExpectedFromScanned}
              generatingExpectedFromScanned={generatingExpectedFromScanned}
              onSaveExpectedPurchasePrice={saveExpectedPurchasePrice}
              savingExpectedPurchasePriceId={savingExpectedPurchasePriceId}
            />
          </div>
        </div>

        <div className='row'>
          <div className='col p-0'>
            <Footer />
          </div>
        </div>
      </div>
      <IncomingShipmentExcelImportModal
        api={shipmentApi}
        open={excelImportOpen}
        shipmentId={shipmentId}
        onClose={() => setExcelImportOpen(false)}
        onImported={(payload) => {
          setShipment((prev) => mergeDetailApiPayload(prev, Array.isArray(payload) ? { expectedItems: payload } : payload));
        }}
      />
      <IncomingDeleteItemConfirmModal
        open={deleteConfirmExpectedId != null}
        title='Видалити з очікуваних?'
        highlight={deleteExpectedHighlight}
        onCancel={cancelDeleteExpectedItem}
        onConfirm={confirmDeleteExpectedItem}
        submitting={Boolean(deleteConfirmExpectedId && deletingExpectedId === deleteConfirmExpectedId)}
      />

      {postingAllReady && (
        <div
          className='position-fixed d-flex align-items-center justify-content-center'
          role='status'
          aria-busy='true'
          aria-live='polite'
          style={{
            inset: 0,
            zIndex: 2000,
            background: 'rgba(15, 23, 42, 0.45)',
            backdropFilter: 'blur(2px)'
          }}
        >
          <div
            className='bg-white rounded shadow-lg text-center px-4 py-4 mx-3'
            style={{ maxWidth: 'min(26rem, 92vw)' }}
          >
            <div className='spinner-border text-primary mb-3' aria-hidden='true' />
            <p className='mb-0 text-dark' style={{ fontSize: '0.95rem', lineHeight: 1.5 }}>
              Викладаємо товари з надходження. Спочатку в існуючі замовлення, потім в надходження
            </p>
          </div>
        </div>
      )}
    </>
  );
};

const IncomingShipmentDetailPage = props => {
  const { shipmentId } = useParams();
  return <IncomingShipmentDetail {...props} key={shipmentId} />;
};

export default IncomingShipmentDetailPage;
